import { Admin, Resource, CustomRoutes, fetchUtils } from 'react-admin';
import { Route } from 'react-router-dom';
import simpleRestProvider from 'ra-data-simple-rest';
import { API_URL } from './api';

import CustomerList   from './customers/CustomerList';
import CustomerCreate from './customers/CustomerCreate';
import CustomerEdit   from './customers/CustomerEdit';
import CustomerShow   from './customers/CustomerShow';

import ProductList   from './products/ProductList';
import ProductCreate from './products/ProductCreate';
import ProductEdit   from './products/ProductEdit';
import ProductShow   from './products/ProductShow';

import AttributeCreate from './attributes/AttributeCreate';
import AttributeList   from './attributes/AttributeList';

import OrderList   from './orders/OrderList';
import OrderCreate from './orders/OrderCreate';
import OrderEdit   from './orders/OrderEdit';

import PaymentList   from './payments/PaymentList';
import PaymentCreate from './payments/PaymentCreate';
import PaymentEdit   from './payments/PaymentEdit';

import VariantCreate from './variants/VariantCreate';
import VariantEdit   from './variants/VariantEdit';

import LeadList   from './leads/LeadList';
import LeadCreate from './leads/LeadCreate';
import LeadEdit   from './leads/LeadEdit';

import SupplierList   from './suppliers/SupplierList';
import SupplierCreate from './suppliers/SupplierCreate';
import SupplierEdit   from './suppliers/SupplierEdit';
import SupplierShow   from './suppliers/SupplierShow';

import WorkerList   from './workers/WorkerList';
import WorkerCreate from './workers/WorkerCreate';
import WorkerEdit   from './workers/WorkerEdit';

import PurchaseList   from './purchases/PurchaseList';
import PurchaseCreate from './purchases/PurchaseCreate';
import PurchaseEdit   from './purchases/PurchaseEdit';


import ProfitReport         from './reports/ProfitReport';
import InventoryView        from './inventory/InventoryView';
import WorkRates            from './workers/WorkRates';
import TasksPage            from './tasks/TasksPage';
import AuditLogPage         from './audit/AuditLogPage';
import AssistantPage        from './assistant/AssistantPage';
import UserList             from './users/UserList';
import UserCreate           from './users/UserCreate';
import UserEdit             from './users/UserEdit';
import ProductionList       from './production/ProductionList';
import ProductionCreate     from './production/ProductionCreate';
import ProductionShow       from './production/ProductionShow';
import MatchingRulesList  from './matching_rules/MatchingRulesList';
import MatchingRuleCreate from './matching_rules/MatchingRuleCreate';
import MatchingRuleEdit   from './matching_rules/MatchingRuleEdit';

import WebsiteCategoryList from './website/WebsiteCategoryList';
import { WebsiteCategoryCreate, WebsiteCategoryEdit } from './website/WebsiteCategoryForm';
import WebsiteProductList   from './website/WebsiteProductList';
import WebsiteProductCreate from './website/WebsiteProductCreate';
import WebsiteProductEdit   from './website/WebsiteProductEdit';
import { WebsiteMappingIssueList } from './website/WebsiteMappingIssues';
import WebsiteBestSellersPage from './website/WebsiteBestSellersPage';
import WebsiteSettingsPage from './website/WebsiteSettingsPage';
import { WebsitePageList, WebsitePageEdit } from './website/WebsitePages';
import { WebsiteAccountList, WebsiteAccountShow } from './website/WebsiteAccounts';
import { WebsiteCouponList, WebsiteCouponCreate, WebsiteCouponEdit } from './website/WebsiteCoupons';
import WebsiteShippingPage from './website/WebsiteShippingPage';

import Dashboard    from './dashboard/Dashboard';
import CustomLayout from './layout/CustomLayout';
import theme        from './styles/theme';
import authProvider       from './auth/authProvider';
import LoginPage          from './auth/LoginPage';
import ChangePasswordPage from './auth/ChangePasswordPage';

const httpClient = (url, options = {}) => {
  const headers = new Headers(options.headers || {});
  const token   = localStorage.getItem('auth_token');
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return fetchUtils.fetchJson(url, { ...options, headers }).catch(err => {
    // ra-core's HttpError takes its .message from json.message — our backend
    // always sends { error: '...' } instead, so without this every default
    // (no custom catch) react-admin error notification shows a generic
    // "Bad Request"/"Conflict" rather than the server's actual message.
    if (err?.body?.error) err.message = err.body.error;
    throw err;
  });
};

const dataProvider = simpleRestProvider(API_URL, httpClient);

export default function App() {
  return (
    <Admin
      dataProvider={dataProvider}
      authProvider={authProvider}
      loginPage={LoginPage}
      theme={theme}
      dashboard={Dashboard}
      layout={CustomLayout}
      requireAuth
    >
      {permissions => (
        <>
          <Resource name="customers"        list={CustomerList}        create={CustomerCreate}        edit={CustomerEdit}        show={CustomerShow} />
          <Resource name="orders"           list={OrderList}           create={OrderCreate}           edit={OrderEdit} />
          <Resource name="payments"         list={PaymentList}         create={PaymentCreate}         edit={PaymentEdit} />
          <Resource name="products"         list={ProductList}         create={ProductCreate}         edit={ProductEdit}         show={ProductShow} />
          <Resource name="attributes"       list={AttributeList}       create={AttributeCreate} />
          <Resource name="variants"         create={VariantCreate}     edit={VariantEdit} />
          <Resource name="order_items" />
          <Resource name="leads"            list={LeadList}            create={LeadCreate}            edit={LeadEdit} />
          <Resource name="suppliers"        list={SupplierList}        create={SupplierCreate}        edit={SupplierEdit}        show={SupplierShow} />
          <Resource name="workers"          list={WorkerList}          create={WorkerCreate}          edit={WorkerEdit} />
          <Resource name="purchases"        list={PurchaseList}        create={PurchaseCreate}        edit={PurchaseEdit} />

          <Resource name="worker_roles" />
          <Resource name="tags" />
          <Resource name="matching_rule_sets" />
          <Resource name="matching_rules"     list={MatchingRulesList} create={MatchingRuleCreate} edit={MatchingRuleEdit} />
          <Resource name="production_batches" list={ProductionList} create={ProductionCreate} show={ProductionShow} />
          <Resource name="stock_movements" />
          {permissions === 'admin' && (
            <Resource name="users" list={UserList} create={UserCreate} edit={UserEdit} />
          )}
          {/* Website Management — admin only; the server enforces it too (requireRole('admin')). */}
          {permissions === 'admin' && (
            <>
              <Resource name="website_categories"     list={WebsiteCategoryList} create={WebsiteCategoryCreate} edit={WebsiteCategoryEdit} />
              <Resource name="website_products"       list={WebsiteProductList}  create={WebsiteProductCreate}  edit={WebsiteProductEdit} />
              <Resource name="website_mapping_issues" list={WebsiteMappingIssueList} />
              <Resource name="website_pages"          list={WebsitePageList} edit={WebsitePageEdit} />
              <Resource name="website_accounts"       list={WebsiteAccountList} show={WebsiteAccountShow} />
              <Resource name="website_coupons"        list={WebsiteCouponList} create={WebsiteCouponCreate} edit={WebsiteCouponEdit} />
            </>
          )}
          <CustomRoutes>
            <Route path="/change-password" element={<ChangePasswordPage />} />
            <Route path="/profit-report"                element={<ProfitReport />} />
            <Route path="/inventory"                    element={<InventoryView />} />
            <Route path="/work-rates"                   element={<WorkRates />} />
            <Route path="/tasks"                        element={<TasksPage />} />
            {permissions === 'admin' && <Route path="/audit" element={<AuditLogPage />} />}
            {permissions === 'admin' && <Route path="/assistant" element={<AssistantPage />} />}
            {permissions === 'admin' && <Route path="/website_best_sellers" element={<WebsiteBestSellersPage />} />}
            {permissions === 'admin' && <Route path="/website_settings" element={<WebsiteSettingsPage />} />}
            {permissions === 'admin' && <Route path="/website_shipping" element={<WebsiteShippingPage />} />}
            <Route path="/production_batches/:id/show" element={<ProductionShow />} />
          </CustomRoutes>
        </>
      )}
    </Admin>
  );
}
